import React from "react";
import { Container, Grid, Typography, Box, Card, Chip, Stack, Button } from "@mui/material";
import CloudSyncIcon from "@mui/icons-material/CloudSync";
import HomeRepairServiceIcon from "@mui/icons-material/HomeRepairService";
import ArrowForwardIcon from "@mui/icons-material/ArrowForward";
import { Link as ScrollLink } from "react-scroll";
import SectionHeading from "./SectionHeading";
import useReveal from "./useReveal";
import { brand } from "../theme";

const products = [
  {
    icon: <CloudSyncIcon />,
    name: "Zealous Profoundry",
    purpose: "Assessment & Learning Enablement Platform",
    desc: "A proprietary cloud application that scales modern technical education. Beyond a standard LMS, Profoundry embeds a containerized automated coding sandbox where students write, compile, and execute code dynamically — while the system evaluates logic efficiency, monitors syntax errors, and tracks plagiarism.",
    tags: ["Cloud LMS", "Coding Sandbox", "Auto-Evaluation", "Plagiarism Tracking"],
    accent: brand.blue,
  },
  {
    icon: <HomeRepairServiceIcon />,
    name: "Zealous Electrocare",
    purpose: "Technology-Enabled Home Service Platform",
    desc: "A commercial web & mobile marketplace modernizing localized technician matching. Built on a decoupled Python FastAPI + MongoDB backend, it features optimized matching algorithms, location-aware tracking, and secure payments — giving our interns direct experience managing live user traffic.",
    tags: ["FastAPI", "MongoDB", "Geo-Matching", "Secure Payments"],
    accent: brand.orange,
  },
];

const Products = () => {
  const ref = useReveal();
  return (
    <section id="products-section" className="products-section" ref={ref}>
      <Box sx={{ py: { xs: 7, md: 11 }, background: brand.bgAlt }}>
        <Container>
          <SectionHeading
            eyebrow="PROPRIETARY PRODUCT PORTFOLIO"
            title="Products We Build & Operate"
            subtitle="Our product division designs, launches, and operates scalable proprietary platforms — concrete proof of our technical architecture capabilities."
          />
          <Grid container spacing={4} className="reveal-stagger">
            {products.map((p) => (
              <Grid item xs={12} md={6} key={p.name}>
                <Card
                  elevation={0}
                  sx={{
                    height: "100%",
                    p: { xs: 3, md: 4 },
                    bgcolor: "#fff",
                    border: "1px solid rgba(12,131,200,0.10)",
                    position: "relative",
                    overflow: "hidden",
                    transition: "transform .3s ease, box-shadow .3s ease",
                    "&:hover": { transform: "translateY(-6px)", boxShadow: "0 22px 48px rgba(15,39,64,0.12)" },
                    "&::before": {
                      content: '""',
                      position: "absolute",
                      top: 0,
                      left: 0,
                      width: "100%",
                      height: 5,
                      background: p.accent,
                    },
                  }}
                >
                  <Box
                    sx={{
                      width: 64,
                      height: 64,
                      borderRadius: "16px",
                      display: "flex",
                      alignItems: "center",
                      justifyContent: "center",
                      bgcolor: `${p.accent}1A`,
                      color: p.accent,
                      mb: 2.5,
                      "& svg": { fontSize: 34 },
                    }}
                  >
                    {p.icon}
                  </Box>
                  <Typography variant="h5" sx={{ color: brand.navy, fontWeight: 800, mb: 0.5 }}>
                    {p.name}
                  </Typography>
                  <Typography sx={{ color: p.accent, fontWeight: 600, mb: 2, fontSize: "0.95rem" }}>
                    {p.purpose}
                  </Typography>
                  <Typography variant="body2" sx={{ color: brand.slate, mb: 2.5 }}>
                    {p.desc}
                  </Typography>
                  <Stack direction="row" flexWrap="wrap" gap={1}>
                    {p.tags.map((t) => (
                      <Chip
                        key={t}
                        label={t}
                        size="small"
                        sx={{ bgcolor: brand.bgAlt, color: brand.navy, fontWeight: 600, fontSize: "0.76rem" }}
                      />
                    ))}
                  </Stack>
                </Card>
              </Grid>
            ))}
          </Grid>

          <Box sx={{ textAlign: "center", mt: 5 }} className="reveal">
            <Button variant="contained" color="primary" size="large" endIcon={<ArrowForwardIcon />}>
              <ScrollLink to="contacts-section" smooth={true} duration={800} style={{ color: "inherit" }}>
                Request a Product Demo
              </ScrollLink>
            </Button>
          </Box>
        </Container>
      </Box>
    </section>
  );
};

export default Products;
